<template id="4wq5e6">
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container">
      <div class="modal-header">
        <h6>Detalhar ajuste da operadora</h6>
        <i class="bi bi-x-circle close-icon" @click="$emit('close')"></i>
      </div>

      <div class="spacer"></div>

      <div class="modal-content">
        <div class="info-box mb-3">
          <h6 class="section-title">Resumo do ajuste</h6>
          <div><strong>Por que está nesta aba:</strong> {{ item?.motivo_aba || 'Item da operadora marcado como evento de ajuste.' }}</div>
          <div><strong>Tipo do evento:</strong> {{ item?.descricao_evento || eventoLabel(item?.tipo_evento) }}</div>
          <div><strong>Operadora:</strong> {{ item?.operadora_nome || '—' }}</div>
          <div><strong>Data de referência da conciliação:</strong> {{ formatDate(dataReferencia(item)) }}</div>
          <div><strong>Autorização:</strong> {{ item?.autorizacao || '—' }}</div>
          <div><strong>Valor principal do evento:</strong> {{ formatCurrency(valorPrincipal(item)) }}</div>
          <div><strong>Status:</strong> {{ normalizeStatus(item?.status_conciliacao || 'PENDENTE_AJUSTE') }}</div>
          <div><strong>Tratado em:</strong> {{ formatDateTime(item?.ajuste_tratado_em) }}</div>
          <div><strong>Tratado por:</strong> {{ item?.ajuste_tratado_por_nome || '—' }}</div>
        </div>

        <div class="row">
          <div class="col-md-6 mb-3">
            <div class="info-box h-100">
              <h6 class="section-title">Dados da transação</h6>
              <div><strong>ID item:</strong> {{ item?.id ?? '—' }}</div>
              <div><strong>Data da venda:</strong> {{ formatDate(item?.data_venda) }}</div>
              <div><strong>Data/hora da venda:</strong> {{ formatDateTime(item?.data_hora_venda) }}</div>
              <div><strong>Data de vencimento:</strong> {{ formatDate(item?.data_vencimento) }}</div>
              <div><strong>Data de referência da aba:</strong> {{ formatDate(dataReferencia(item)) }}</div>
              <div><strong>Evento de ajuste:</strong> {{ boolLabel(item?.evento_ajuste) }}</div>
            </div>
          </div>

          <div class="col-md-6 mb-3">
            <div class="info-box h-100">
              <h6 class="section-title">Cartão e identificação</h6>
              <div><strong>Autorização:</strong> {{ item?.autorizacao || '—' }}</div>
              <div><strong>NSU:</strong> {{ item?.nsu || '—' }}</div>
              <div><strong>Cartão mascarado:</strong> {{ item?.cartao_mascarado || '—' }}</div>
              <div><strong>Parcela:</strong> {{ parcelaLabel(item) }}</div>
              <div><strong>Modalidade:</strong> {{ item?.modalidade || '—' }}</div>
              <div><strong>Bandeira:</strong> {{ item?.bandeira || '—' }}</div>
            </div>
          </div>
        </div>

        <div class="info-box mb-3">
          <h6 class="section-title">Dados técnicos da operadora</h6>
          <div class="text-muted small mb-2">
            Estes são os valores brutos retornados pela operadora para o evento. Em ajustes como chargeback,
            cancelamento, compensação ou reversão, eles podem não ter a mesma leitura de uma venda normal.
          </div>

          <div class="row">
            <div class="col-md-6"><strong>Valor principal do evento:</strong> {{ formatCurrency(valorPrincipal(item)) }}</div>
            <div class="col-md-6"><strong>Valor bruto:</strong> {{ formatCurrency(item?.valor_bruto) }}</div>
            <div class="col-md-6"><strong>Valor da parcela:</strong> {{ formatCurrency(item?.valor_parcela) }}</div>
            <div class="col-md-6"><strong>Valor do desconto:</strong> {{ formatCurrency(item?.valor_desconto) }}</div>
            <div class="col-md-6"><strong>Valor líquido:</strong> {{ formatCurrency(item?.valor_liquido) }}</div>
            <div class="col-md-6"><strong>Diferença bruto/líquido:</strong> {{ formatCurrency(item?.diferenca_bruto_liquido) }}</div>
          </div>
        </div>

        <div class="info-box mb-3">
          <h6 class="section-title">Tratamento</h6>
          <div><strong>Status atual:</strong> {{ normalizeStatus(item?.status_conciliacao || 'PENDENTE_AJUSTE') }}</div>
          <div><strong>Tratado em:</strong> {{ formatDateTime(item?.ajuste_tratado_em) }}</div>
          <div><strong>Tratado por:</strong> {{ item?.ajuste_tratado_por_nome || '—' }}</div>
          <div><strong>Observação do tratamento:</strong> {{ item?.ajuste_observacao || '—' }}</div>
        </div>

        <div class="info-box mb-3">
          <h6 class="section-title">Observações</h6>
          <div class="observacoes-box">
            {{ item?.observacoes || '—' }}
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-secondary" @click="$emit('close')">
          Fechar
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
  item: {
    type: Object,
    required: true
  }
})

function formatDate(v) {
  if (!v) return '—'
  const [y, m, d] = String(v).slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

function formatDateTime(v) {
  if (!v) return '—'

  const s = String(v).trim()

  // Se vier apenas a data, evita new Date() para não sofrer com fuso
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-')
    return `${d}/${m}/${y} 00:00`
  }

  // Se vier datetime começando em ISO, tenta preservar o caso inválido
  const dt = new Date(s)
  if (Number.isNaN(dt.getTime())) return String(v)

  const dia = String(dt.getDate()).padStart(2, '0')
  const mes = String(dt.getMonth() + 1).padStart(2, '0')
  const ano = dt.getFullYear()
  const hora = String(dt.getHours()).padStart(2, '0')
  const minuto = String(dt.getMinutes()).padStart(2, '0')

  return `${dia}/${mes}/${ano} ${hora}:${minuto}`
}

function formatCurrency(v) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number(v || 0))
}

function boolLabel(v) {
  return v ? 'Sim' : 'Não'
}

function normalizeStatus(v) {
  const map = {
    PENDENTE: 'Pendente',
    CONCILIADO_AUTOMATICO: 'Conciliado automático',
    CONCILIADO_MANUAL: 'Conciliado manual',
    PENDENTE_AJUSTE: 'Pendente ajuste',
    IGNORADO: 'Ignorado',
    DIVERGENTE: 'Divergente'
  }

  const key = String(v || '').toUpperCase()
  return map[key] || String(v || '').replaceAll('_', ' ')
}

function eventoLabel(v) {
  const s = String(v || '').toUpperCase()
  if (s === 'CHARGEBACK') return 'Chargeback'
  if (s === 'CANCELAMENTO') return 'Cancelamento'
  return s || 'Evento'
}

function parcelaLabel(item) {
  if (!item?.parcela_numero) return '—'
  return `${item.parcela_numero}/${item.parcela_total || item.parcela_numero}`
}

function dataReferencia(item) {
  return item?.data_referencia || item?.data_vencimento || item?.data_venda || null
}

function valorPrincipal(item) {
  if (item?.valor_evento != null) return item.valor_evento
  if (item?.valor_bruto != null) return item.valor_bruto
  if (item?.valor_parcela != null) return item.valor_parcela
  if (item?.valor_liquido != null) return item.valor_liquido
  return 0
}
</script>

<style scoped>
.modal-header {
  background: #ccc;
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  background: #fff;
  width: 100%;
  max-width: 960px;
  max-height: 90vh;
  margin: 0 10px;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-content {
  padding: 15px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.close-icon {
  font-size: 1.5rem;
  cursor: pointer;
}

.spacer {
  margin-bottom: 20px;
}

.section-title {
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 10px;
}

.info-box {
  border: 1px solid #ced4da;
  padding: 10px 12px;
  background: #f8f9fa;
}

.observacoes-box {
  white-space: pre-wrap;
  word-break: break-word;
}

.modal-footer {
  padding: 10px 15px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background: #fff;
  border-top: 1px solid #ced4da;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .modal-container {
    max-width: 100%;
    max-height: 95vh;
  }
}
</style>